import { ApiError, NotFoundError } from "~/api/errors";
import { FIXTURE_ACTOR_ID, evidence as fixtureEvidence } from "~/api/reasoningFixtures";
import { reasoningRequestIds } from "~/api/reasoningRequestIds";
import {
  type DreamerExperimentArtifact,
  type FlowPostmortem,
  REASONING_CHAIN_ERRORS,
  REASONING_CHAIN_OPS,
  REASONING_EFFORTS,
  REASONING_SCHEMA,
  REASONING_STEP_KINDS,
  type ReasoningApi,
  type ReasoningBranchGroup,
  type ReasoningBranchReceipt,
  type ReasoningChainEntry,
  type ReasoningChainOp,
  type ReasoningChainView,
  type ReasoningCommandInput,
  type ReasoningCriterion,
  type ReasoningEvidence,
  type ReasoningMemoryGraph,
  type ReasoningOperation,
  type ReasoningReceipt,
  type ReasoningRecord,
  type ReasoningRetainedArtifact,
  type ReasoningSearchState,
  type ReasoningSession,
  type ReasoningSourceBarrier,
  type ReasoningStepKind,
  type ReasoningSupport,
  type ReasoningTrace,
  type ReasoningTraceEvent,
  type ReasoningTraceOperation,
} from "~/api/reasoningTypes";

/**
 * The reasoning engine in memory, shaped like `ReasoningController`: revision-fenced commands
 * with idempotent receipts, a background request job that advances one state per status poll,
 * a trace with its manifest, and the Dreamer's postmortem/experiment ledgers. Fake-mode `/reasoning`
 * and the run tab work against it, and component tests drive it instead of spying every method.
 */
export interface FakeReasoningOptions {
  /** `requestStatus` polls a background request needs before it completes (queued → running → completed). */
  ticksToComplete?: number;
  /** Sessions to seed (keyed by their `runId` and `id`). */
  sessions?: ReasoningSession[];
  now?: () => string;
  /** The identity the fake writes as every record's actor. */
  actorId?: string;
}

type Job = {
  runId: string;
  sessionId: string;
  status: string;
  error: string | null;
  cancelRequested: boolean;
  ticks: number;
  thinkerId: string;
  model: string;
  effort: string;
  effortMode: string;
};

export const FAKE_REASONING_SUPPORT: ReasoningSupport = {
  schemaVersion: REASONING_SCHEMA,
  supportedBranchAgents: ["ClaudeCode"],
  strategyVersion: "sequential/1",
  capabilities: [
    "context",
    "candidate",
    "evaluate",
    "accept",
    "replan",
    "correct",
    "branch",
    "retained-artifacts",
    "bounded-tree-search",
    "bounded-graph-search",
  ],
  executionHost: "server",
  domain: "knowledge-response",
  flowNodeKind: "reasoning",
  localExecution: {
    optInField: "localOnly",
    provider: "ollama",
    providers: ["ollama", "mlx"],
    effortChoices: [...REASONING_EFFORTS],
    effortModes: ["budgeted", "native"],
    budgetedOutputLimits: { low: 512, medium: 1024, high: 2048, max: 4096 },
    note: "MLX requires budgeted effort. Ollama native effort must be verified from model metadata.",
  },
  maxReasoningNodesPerFlow: 1,
  limits: {
    maxRecordBytes: 256 * 1024,
    maxRetainedArtifactBytes: 8 * 1024 * 1024,
    maxRunRetainedArtifactBytes: 32 * 1024 * 1024,
    maxSearchCandidates: 12,
    maxSearchWidth: 4,
    maxSearchDepth: 6,
    sessionTokenBudget: 262_144,
    maxServerOperations: 16,
    maxIndependentBranches: 8,
    causalAttentionMaxProjectTiles: 40,
    causalAttentionWindowDays: 60,
  },
  unsupportedProfiles: ["corpus-generation"],
};

/** A refusal with its wire code on the error, as `KnowItApi.errorOf` keeps it (`error.body.error`). */
const refuse = (code: string, message: string, status: number): ApiError =>
  Object.assign(new ApiError(message, status), { body: { error: code, message } });

/** `ReasoningChainBuilder.SummaryLimit` — a longer summary is clipped and flagged `truncated`. */
export const CHAIN_SUMMARY_LIMIT = 12_000;
/** `ReviseReasoningChainRequest` — a replacement or inserted step carries 1–4000 characters. */
export const CHAIN_EDIT_SUMMARY_LIMIT = 4000;

/** What the chain builder needs to know about an operation (`ReasoningChainOperation`). */
export interface ChainOperation {
  id: string;
  createdAt: string;
  status: string;
  candidateRecordId: string | null;
  thinking: string | null;
  thinkingTruncated: boolean;
}

/** `ReasoningChainIds.Thinking`: the operation id without dashes, framed. */
export const thinkingEntryId = (operationId: string) =>
  `operation:${operationId.replace(/-/g, "").toLowerCase()}:thinking`;

type ChainDraft = ReasoningChainEntry & {
  chainOp: string | null;
  targetId: string | null;
  afterId: string | null;
  supersededById: string | null;
  strikeMarker: boolean;
  seq: number;
};

/**
 * `ReasoningChainBuilder.Build`, ported: steps and candidates become entries, every completed
 * operation with thinking text adds a `thinking` entry, the base order is creation time with a
 * thinking entry before the candidate its operation produced, and the edit steps are applied in
 * creation order — replace swaps the target's slot and marks it superseded, strike removes it,
 * insert places after `afterId` (or at the start). History is never rewritten: a superseded
 * entry stays in `entries` with `position: -1`. The server breaks a timestamp tie on the id;
 * this clock has no sub-millisecond precision, so insertion order stands in for that tie.
 */
export function buildReasoningChain(
  sessionId: string,
  sessionRevision: number,
  records: ReasoningRecord[],
  operations: ChainOperation[],
): ReasoningChainView {
  const text = (value: unknown) => (typeof value === "string" ? value : "");
  const nullable = (value: unknown) => (typeof value === "string" ? value : null);
  const clip = (value: string) => ({
    summary: value.length > CHAIN_SUMMARY_LIMIT ? value.slice(0, CHAIN_SUMMARY_LIMIT) : value,
    truncated: value.length > CHAIN_SUMMARY_LIMIT,
  });
  const drafts: ChainDraft[] = [];
  const base = {
    position: -1,
    operationId: null,
    candidateRecordId: null,
    supersededByRecordId: null,
    struck: false,
    revisionOfId: null,
    insertedAfterId: null,
    supersededById: null,
    strikeMarker: false,
  };
  records.forEach((record, seq) => {
    if (record.kind !== "step" && record.kind !== "candidate") return;
    const isStep = record.kind === "step";
    const p = record.payload;
    const clipped = clip(text(isStep ? p.summary : p.content));
    drafts.push({
      ...base,
      id: record.id,
      kind: isStep ? "step" : "candidate",
      stepKind: isStep ? nullable(p.kind) : null,
      ...clipped,
      rationale: nullable(isStep ? p.rationaleSummary : p.rationale),
      origin: isStep ? "actor-report" : "server-operation",
      createdAt: record.createdAt,
      recordId: record.id,
      eligible: record.eligible,
      evidence: Array.isArray(p.evidence) ? (p.evidence as ReasoningEvidence[]) : [],
      chainOp: isStep ? nullable(p.chainOp) : null,
      targetId: isStep ? nullable(p.targetId) : null,
      afterId: isStep ? nullable(p.afterId) : null,
      seq,
    });
  });
  operations.forEach((operation, seq) => {
    if (!operation.thinking?.trim()) return;
    const clipped = clip(operation.thinking);
    drafts.push({
      ...base,
      id: thinkingEntryId(operation.id),
      kind: "thinking",
      stepKind: "model-thinking",
      summary: clipped.summary,
      truncated: clipped.truncated || operation.thinkingTruncated,
      rationale: null,
      origin: "model-emitted",
      createdAt: operation.createdAt,
      recordId: null,
      operationId: operation.id,
      candidateRecordId: operation.candidateRecordId,
      eligible: true,
      evidence: [],
      chainOp: null,
      targetId: null,
      afterId: null,
      seq,
    });
  });
  const ordered = [...drafts].sort(
    (a, b) =>
      a.createdAt.localeCompare(b.createdAt) ||
      (a.kind === "thinking" ? 0 : 1) - (b.kind === "thinking" ? 0 : 1) ||
      a.seq - b.seq,
  );
  const byId = new Map(ordered.map((entry) => [entry.id, entry]));
  const order = ordered.filter((entry) => entry.chainOp === null).map((entry) => entry.id);
  for (const op of ordered.filter((entry) => entry.chainOp !== null)) {
    const target = op.targetId ? byId.get(op.targetId) : undefined;
    if (op.chainOp === "replace" && target) {
      const at = order.indexOf(target.id);
      if (at < 0 || target.supersededById !== null || target.struck) {
        order.push(op.id);
        continue;
      }
      target.supersededById = op.id;
      op.revisionOfId = target.id;
      order[at] = op.id;
    } else if (op.chainOp === "strike" && target) {
      const at = order.indexOf(target.id);
      if (at >= 0 && target.supersededById === null && !target.struck) {
        target.struck = true;
        target.supersededById = op.id;
        order.splice(at, 1);
      }
      op.revisionOfId = target.id;
      op.strikeMarker = true;
    } else if (op.chainOp === "insert") {
      op.insertedAfterId = op.afterId;
      const at = op.afterId === null ? -1 : order.indexOf(op.afterId);
      if (op.afterId !== null && at < 0) order.push(op.id);
      else order.splice(at + 1, 0, op.id);
    } else order.push(op.id);
  }
  const positions = new Map(order.map((id, index) => [id, index]));
  const isRecordId = (id: string | null) => id !== null && !id.startsWith("operation:");
  const entries: ReasoningChainEntry[] = ordered.map(
    ({
      chainOp: _op,
      targetId: _t,
      afterId: _a,
      supersededById,
      strikeMarker: _m,
      seq: _s,
      ...x
    }) => ({
      ...x,
      position: positions.get(x.id) ?? -1,
      supersededByRecordId: isRecordId(supersededById) ? supersededById : null,
    }),
  );
  const effective = order.filter((id) => !byId.get(id)?.strikeMarker);
  return {
    sessionId,
    sessionRevision,
    entries,
    effective,
    digest: fakeDigest(effective.join("\n")),
  };
}

/** A deterministic 64-hex "digest": the shape the evidence schema demands, never a real hash. */
export function fakeDigest(text: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  const seed = `${h1.toString(16).padStart(8, "0")}${h2.toString(16).padStart(8, "0")}`;
  return seed.repeat(4);
}

export function createFakeReasoning(options: FakeReasoningOptions = {}): ReasoningApi {
  const ticksToComplete = options.ticksToComplete ?? 2;
  const now = options.now ?? (() => new Date().toISOString());
  const actorId = options.actorId ?? FIXTURE_ACTOR_ID;
  const uuid = () => crypto.randomUUID();

  const sessions = new Map<string, ReasoningSession>();
  const runs = new Map<string, string>();
  const barriers = new Map<string, ReasoningSourceBarrier[]>();
  const events = new Map<string, ReasoningTraceEvent[]>();
  const operations = new Map<string, ReasoningTraceOperation[]>();
  const jobs = new Map<string, Job>();
  const groups = new Map<string, ReasoningBranchGroup[]>();
  const postmortems = new Map<string, FlowPostmortem>();
  const experiments = new Map<string, DreamerExperimentArtifact[]>();
  const receipts = new Map<string, ReasoningReceipt>();
  const artifacts = new Map<string, ReasoningRetainedArtifact>();
  const artifactCommands = new Map<string, string>();
  const searches = new Map<string, ReasoningSearchState>();
  const branchReceipts = new Map<string, ReasoningBranchReceipt>();
  /** The thinking a model emitted per operation — kept off the trace row, read by the chain. */
  const thinking = new Map<string, string>();

  for (const seed of options.sessions ?? []) {
    sessions.set(seed.id, seed);
    runs.set(seed.runId, seed.id);
    events.set(
      seed.id,
      seed.records.map((row, index) => ({
        id: `event-${seed.id}-${index + 1}`,
        sequence: index + 1,
        kind: `${row.kind}-recorded`,
        payload: { recordId: row.id, summary: `${row.kind} ${row.logicalId}` },
        recordId: row.id,
        actorId,
        occurredAt: row.createdAt,
      })),
    );
  }

  const requireSession = (sessionId: string): ReasoningSession => {
    const session = sessions.get(sessionId);
    if (!session) throw new NotFoundError("The reasoning session does not exist.");
    return session;
  };
  const fence = (session: ReasoningSession, expected: number) => {
    if (expected !== session.revision)
      throw refuse(
        REASONING_CHAIN_ERRORS.revisionConflict,
        `The session is at revision ${session.revision}, not ${expected}. Reload and retry.`,
        409,
      );
  };
  /** The chain as the server derives it, from the retained records and the operations' thinking. */
  const chainOf = (session: ReasoningSession): ReasoningChainView =>
    buildReasoningChain(
      session.id,
      session.revision,
      session.records,
      (operations.get(session.id) ?? []).map((op) => ({
        id: op.id,
        createdAt: op.createdAt ?? now(),
        status: op.status,
        candidateRecordId: op.candidateRecordId,
        thinking: thinking.get(op.id) ?? null,
        thinkingTruncated: false,
      })),
    );
  const event = (
    session: ReasoningSession,
    kind: string,
    payload: Record<string, unknown>,
    recordId: string | null = null,
    commandId?: string,
  ): ReasoningTraceEvent => {
    const list = events.get(session.id) ?? [];
    const row: ReasoningTraceEvent = {
      id: uuid(),
      sequence: list.length + 1,
      kind,
      payload,
      recordId,
      actorId,
      commandId: commandId ?? uuid(),
      occurredAt: now(),
    };
    list.push(row);
    events.set(session.id, list);
    return row;
  };
  const append = (
    session: ReasoningSession,
    kind: string,
    logicalId: string,
    payload: Record<string, unknown>,
    commandId: string,
    revision = 1,
  ): ReasoningRecord => {
    const row: ReasoningRecord = {
      id: uuid(),
      kind,
      logicalId,
      revision,
      schemaVersion: REASONING_SCHEMA,
      payload,
      payloadDigest: fakeDigest(JSON.stringify(payload)),
      actorId,
      createdAt: now(),
      eligible: true,
      ineligibilityReason: null,
    };
    session.records = [...session.records, row];
    session.revision += 1;
    event(
      session,
      `${kind}-recorded`,
      {
        recordId: row.id,
        summary: typeof payload.summary === "string" ? payload.summary : `${kind} ${logicalId}`,
        ...(typeof payload.rationale === "string" ? { rationaleSummary: payload.rationale } : {}),
        ...(Array.isArray(payload.inputRecordIds)
          ? { referencedRecordIds: payload.inputRecordIds }
          : {}),
        ...(Array.isArray(payload.causedByEventIds)
          ? { causedByEventIds: payload.causedByEventIds }
          : {}),
      },
      row.id,
      commandId,
    );
    return row;
  };
  const receipt = (
    input: ReasoningCommandInput,
    session: ReasoningSession,
    recordId: string | null,
    status = "committed",
  ): ReasoningReceipt => {
    const value: ReasoningReceipt = {
      commandId: input.commandId ?? uuid(),
      sessionId: session.id,
      sessionRevision: session.revision,
      recordId,
      status,
      reason: null,
    };
    receipts.set(value.commandId, value);
    return value;
  };
  /** The same command id answers with the same receipt — never a second record. */
  const replay = (input: ReasoningCommandInput): ReasoningReceipt | undefined =>
    input.commandId ? receipts.get(input.commandId) : undefined;
  const contextOf = (session: ReasoningSession) =>
    session.records.find((row) => row.id === session.contextRecordId);
  const pinnedEvidence = (session: ReasoningSession): ReasoningEvidence[] => {
    const value = contextOf(session)?.payload.evidence;
    return Array.isArray(value) ? (value as ReasoningEvidence[]) : [];
  };
  const latestCandidate = (session: ReasoningSession) =>
    session.records.filter((row) => row.kind === "candidate").at(-1);
  const invalidate = (session: ReasoningSession, ids: string[], reason: string) => {
    session.records = session.records.map((row) =>
      ids.includes(row.id) ? { ...row, eligible: false, ineligibilityReason: reason } : row,
    );
    if (session.currentAcceptanceRecordId && ids.includes(session.currentAcceptanceRecordId)) {
      session.currentAcceptanceRecordId = null;
      session.state = "needs-reevaluation";
    }
  };
  /** Everything that depends on a record: evaluations, acceptances, assessments and later revisions. */
  const dependents = (session: ReasoningSession, roots: string[]): string[] => {
    const affected = new Set<string>();
    const queue = [...roots];
    while (queue.length) {
      const id = queue.shift() as string;
      for (const row of session.records) {
        const p = row.payload;
        const depends =
          p.candidateRecordId === id ||
          p.evaluationRecordId === id ||
          p.contextRecordId === id ||
          (Array.isArray(p.derivedFromRecordIds) && p.derivedFromRecordIds.includes(id)) ||
          (Array.isArray(p.inputRecordIds) && p.inputRecordIds.includes(id));
        if (depends && !affected.has(row.id)) {
          affected.add(row.id);
          queue.push(row.id);
        }
      }
    }
    return [...affected];
  };

  const createSession = (
    runId: string,
    sessionId: string,
    commandId: string,
    input: {
      scopeSlug: string;
      query: string;
      model: string;
      modelContextWindow: number;
      outputReserve: number;
      promptReserve?: number;
      requiredNodeIds?: string[];
      criteria: ReasoningCriterion[];
      purpose?: unknown;
      workItemId?: string;
      deadline?: string;
    },
  ): ReasoningSession => {
    const sources = (input.requiredNodeIds?.length ? input.requiredNodeIds : ["source-1"]).map(
      (nodeId, index) => ({
        nodeId,
        path: index === 0 ? "docs/overview.md" : `docs/source-${index + 1}.md`,
        revision: "source-r1",
        required: true,
      }),
    );
    const pinned: ReasoningEvidence[] = sources.map((source) => ({
      ...fixtureEvidence,
      sourceId: source.nodeId,
      revision: source.revision,
    }));
    const manifest = {
      schemaVersion: "reasoning-context/1",
      scopeSlug: input.scopeSlug,
      query: input.query,
      model: input.model,
      modelContextWindow: input.modelContextWindow,
      outputReserve: input.outputReserve,
      promptReserve: input.promptReserve ?? 1024,
      estimatedInputTokens: 250,
      sources,
      memories: [],
      degradation: [],
      digest: fakeDigest(input.query),
    };
    const session: ReasoningSession = {
      id: sessionId,
      runId,
      workItemId: input.workItemId ?? null,
      schemaVersion: REASONING_SCHEMA,
      strategyVersion: "sequential/1",
      revision: 0,
      dependencyEpoch: 0,
      state: "reasoning",
      criteriaDigest: fakeDigest(JSON.stringify(input.criteria)),
      criteria: input.criteria,
      contextRecordId: "",
      currentAcceptanceRecordId: null,
      deadline: input.deadline ?? null,
      records: [],
    };
    sessions.set(sessionId, session);
    runs.set(runId, sessionId);
    const context = append(
      session,
      "context",
      "context",
      { manifest, evidence: pinned, ...(input.purpose ? { purpose: input.purpose } : {}) },
      commandId,
    );
    session.contextRecordId = context.id;
    barriers.set(sessionId, []);
    return session;
  };

  /** One generation + evaluation, as `ReasoningOperationRunner` would record it. */
  const runOperation = (
    session: ReasoningSession,
    input: { thinkerId: string; model: string; effort: string; effortMode?: string },
    commandId: string,
    checkpoint?: { id: string; digest: string },
  ): ReasoningTraceOperation => {
    const context = contextOf(session);
    const manifest = (context?.payload.manifest ?? {}) as { query?: string };
    const query = manifest.query ?? "the question";
    const prior = latestCandidate(session);
    const logicalId = prior?.logicalId ?? "answer";
    const revision = (prior?.revision ?? 0) + 1;
    const opId = uuid();
    const createdAt = now();
    const limits = FAKE_REASONING_SUPPORT.localExecution.budgetedOutputLimits ?? {};
    const outputLimit = limits[input.effort as keyof typeof limits] ?? 1024;
    event(
      session,
      "generation-dispatched",
      {
        operationId: opId,
        model: input.model,
        ...(checkpoint ? { chainCheckpointId: checkpoint.id, chainDigest: checkpoint.digest } : {}),
      },
      null,
      commandId,
    );
    // The model's emitted thinking: generated text the chain shows as "model thinking · unverified".
    thinking.set(
      opId,
      `Reading the pinned sources for “${query}”. Two observations support a cautious conclusion; one assumption has to be named before deciding.${
        checkpoint ? " The pinned chain up to the chosen entry frames this attempt." : ""
      }`,
    );
    const candidate = append(
      session,
      "candidate",
      logicalId,
      {
        content: `Answer to “${query}”: the pinned sources support a cautious conclusion; two observations are cited and one assumption is named.${
          checkpoint ? " (Continued from the edited chain.)" : ""
        }`,
        rationale: checkpoint
          ? "Generated from the pinned context and the chain up to the chosen entry."
          : "Generated from the pinned context only.",
        evidence: pinnedEvidence(session),
        contextRecordId: session.contextRecordId,
        derivedFromRecordIds: prior ? [prior.id] : [],
        evidenceComplete: true,
      },
      commandId,
      revision,
    );
    event(
      session,
      "generation-received",
      { operationId: opId, recordId: candidate.id },
      candidate.id,
    );
    event(
      session,
      "generation-applied",
      { operationId: opId, recordId: candidate.id },
      candidate.id,
    );
    event(session, "evaluation-dispatched", { operationId: opId }, null);
    const evaluation = append(
      session,
      "evaluation",
      `evaluation-${revision}`,
      {
        candidateRecordId: candidate.id,
        contextRecordId: session.contextRecordId,
        criteriaDigest: session.criteriaDigest,
        verdicts: session.criteria.map((criterion) => ({
          criterionId: criterion.id,
          verdict: "pass",
          rationale: "The cited sources carry the claim.",
        })),
        rationale: "Every claim cites a pinned source.",
        qualified: true,
      },
      commandId,
    );
    event(
      session,
      "evaluation-received",
      { operationId: opId, recordId: evaluation.id },
      evaluation.id,
    );
    event(session, "operation-finished", { operationId: opId, status: "completed" }, null);
    const operation: ReasoningTraceOperation = {
      id: opId,
      operationId: opId,
      sessionId: session.id,
      status: "completed",
      thinkerId: input.thinkerId,
      requestedModel: input.model,
      requestedEffort: input.effort,
      contextRecordId: session.contextRecordId,
      criteriaDigest: session.criteriaDigest,
      dependencyEpoch: session.dependencyEpoch,
      reservedTokens: 250 + outputLimit * 2,
      actualInputTokens: 250,
      actualOutputTokens: Math.min(outputLimit, 180),
      inputTokens: 250,
      outputTokens: Math.min(outputLimit, 180),
      candidateRecordId: candidate.id,
      evaluationRecordId: evaluation.id,
      error: null,
      receiptGap: false,
      journalGap: false,
      generation: {
        requestId: uuid(),
        model: input.model,
        inputTokens: 250,
        outputTokens: Math.min(outputLimit, 180),
        durationMs: 1800,
        effortMode: input.effortMode ?? "budgeted",
        outputLimit,
        truncated: false,
        fullTextDigest: candidate.payloadDigest,
        error: null,
      },
      evaluation: {
        requestId: uuid(),
        model: input.model,
        inputTokens: 300,
        outputTokens: 90,
        durationMs: 900,
        effortMode: input.effortMode ?? "budgeted",
        outputLimit,
        truncated: false,
        fullTextDigest: evaluation.payloadDigest,
        error: null,
      },
      createdAt,
      updatedAt: now(),
    };
    operations.set(session.id, [...(operations.get(session.id) ?? []), operation]);
    return operation;
  };

  const groupOf = (sessionId: string, groupId: string): ReasoningBranchGroup => {
    const group = (groups.get(sessionId) ?? []).find((row) => row.id === groupId);
    if (!group) throw new NotFoundError("The branch group does not exist.");
    return group;
  };
  const branchReceipt = (
    input: ReasoningCommandInput,
    session: ReasoningSession,
    groupId: string,
    status: string,
    extra: Partial<ReasoningBranchReceipt> = {},
  ): ReasoningBranchReceipt => {
    const value: ReasoningBranchReceipt = {
      commandId: input.commandId ?? uuid(),
      sessionId: session.id,
      sessionRevision: session.revision,
      groupId,
      status,
      attemptId: null,
      assignmentEpoch: null,
      recordId: null,
      ...extra,
    };
    branchReceipts.set(value.commandId, value);
    return value;
  };
  const experimentArtifact = (
    runId: string,
    experimentId: string,
    name: string,
    content: Record<string, unknown>,
  ): DreamerExperimentArtifact => {
    const artifact = {
      path: `reasoning/experiments/${experimentId}/${name}.json`,
      createdAt: now(),
      content,
    };
    experiments.set(runId, [...(experiments.get(runId) ?? []), artifact]);
    return artifact;
  };

  const api: ReasoningApi = {
    importSource: async () => {
      throw new Error("External source imports require a connected server.");
    },
    getSource: async () => {
      throw new Error("External source imports require a connected server.");
    },
    startSearch: async (input) => {
      const session = requireSession(input.sessionId);
      const prior = searches.get(session.id);
      if (prior) return structuredClone(prior);
      fence(session, input.expectedSessionRevision);
      const state: ReasoningSearchState = {
        searchId: input.commandId ?? uuid(),
        options: input.options,
        contextRecordId: session.contextRecordId,
        criteriaDigest: session.criteriaDigest,
        dependencyEpoch: session.dependencyEpoch,
        startRevision: session.revision,
        revision: 1,
        status: "searching",
        frontier: [{ parents: [], depth: 0, alternative: 0 }],
        candidates: [],
        advanceCommands: [],
        mergedPairs: [],
      };
      searches.set(session.id, state);
      append(
        session,
        "search-checkpoint",
        state.searchId,
        structuredClone(state) as unknown as Record<string, unknown>,
        state.searchId,
      );
      return structuredClone(state);
    },
    advanceSearch: async (input) => {
      const session = requireSession(input.sessionId);
      const state = searches.get(session.id);
      if (!state) throw refuse("search-not-configured", "Configure a search first.", 409);
      if (state.status !== "searching") return structuredClone(state);
      const commandId = input.commandId ?? uuid();
      const operation = await api.execute({
        sessionId: session.id,
        commandId,
        thinkerId: state.options.thinkerId,
        model: state.options.model,
        effort: state.options.effort,
        evaluatorThinkerId: state.options.evaluatorThinkerId,
        evaluatorModel: state.options.evaluatorModel,
        effortMode: "budgeted",
      });
      state.status = "selected-proposal";
      state.revision++;
      state.frontier = [];
      state.advanceCommands.push(commandId);
      state.selectedCandidateId = operation.candidateRecordId;
      state.selectedEvaluationId = operation.evaluationRecordId;
      state.candidates.push({
        recordId: operation.candidateRecordId ?? "",
        evaluationRecordId: operation.evaluationRecordId ?? "",
        depth: 0,
        terminal: true,
        qualified: true,
        contentDigest: "fake-search-content",
        disposition: "selected-proposal",
      });
      append(
        session,
        "search-checkpoint",
        state.searchId,
        structuredClone(state) as unknown as Record<string, unknown>,
        commandId,
        state.revision,
      );
      return structuredClone(state);
    },
    getSearch: async (sessionId) => {
      requireSession(sessionId);
      return structuredClone(searches.get(sessionId) ?? null);
    },
    retainArtifact: async (input) => {
      const session = requireSession(input.sessionId);
      const serialized = JSON.stringify(input);
      const replayed = replay(input);
      if (replayed) {
        if (artifactCommands.get(replayed.commandId) !== serialized)
          throw refuse("command-payload-conflict", "The command payload changed.", 409);
        return replayed;
      }
      fence(session, input.expectedSessionRevision);
      const encoded = new TextEncoder().encode(input.content);
      if (!encoded.length || encoded.length > 8 * 1024 * 1024)
        throw refuse("artifact-too-large", "Retain between 1 byte and 8 MiB.", 422);
      const mediaType = input.mediaType ?? "text/plain";
      const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", encoded));
      fence(session, input.expectedSessionRevision);
      const sha256 = Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join("");
      const artifactId = uuid();
      const record = append(
        session,
        "retained-artifact",
        input.commandId ?? uuid(),
        {
          artifactId,
          sha256,
          bytes: encoded.length,
          mediaType,
          origin: "actor-upload",
          verified: false,
        },
        input.commandId ?? uuid(),
      );
      artifacts.set(record.id, {
        recordId: record.id,
        artifactId,
        sha256,
        bytes: encoded.length,
        mediaType,
        content: input.content,
      });
      const result = receipt(input, session, record.id);
      artifactCommands.set(result.commandId, serialized);
      return result;
    },
    readArtifact: async (sessionId, recordId) => {
      const session = requireSession(sessionId);
      const artifact = artifacts.get(recordId);
      if (!artifact || !session.records.some((record) => record.id === recordId))
        throw new NotFoundError("The retained artifact does not exist.");
      return structuredClone(artifact);
    },
    localCapabilities: async () => ({
      model: "fake:local",
      supportsThinking: true,
      effortLevels: ["low", "medium", "high"],
      nativeLevelsVerified: true,
      maxOutputTokens: 4096,
    }),
    support: async () => structuredClone(FAKE_REASONING_SUPPORT),
    request: async (input) => {
      const commandId = input.commandId ?? uuid();
      const { runId, sessionId } = await reasoningRequestIds(commandId);
      const session = sessions.get(sessionId) ?? createSession(runId, sessionId, commandId, input);
      if (input.background) {
        const job = jobs.get(runId) ?? {
          runId,
          sessionId,
          status: "queued",
          error: null,
          cancelRequested: false,
          ticks: 0,
          thinkerId: input.thinkerId,
          model: input.model,
          effort: input.effort,
          effortMode: input.effortMode ?? "budgeted",
        };
        jobs.set(runId, job);
        return { runId, sessionId, status: job.status, error: job.error };
      }
      const operation =
        operations.get(sessionId)?.at(-1) ?? runOperation(session, input, `${commandId}:execute`);
      return { runId, sessionId, operation, session: structuredClone(session) };
    },
    requestStatus: async (runId) => {
      const job = jobs.get(runId);
      if (!job) throw new NotFoundError("reasoning-request-job-not-found");
      if (!["completed", "failed", "cancelled", "uncertain"].includes(job.status)) {
        if (job.cancelRequested) job.status = "cancelled";
        else {
          job.ticks += 1;
          if (job.ticks >= ticksToComplete) {
            const session = requireSession(job.sessionId);
            if (!operations.get(job.sessionId)?.length)
              runOperation(session, job, `${runId}:execute`);
            job.status = "completed";
          } else job.status = job.ticks === 1 ? "claimed" : "running";
        }
      }
      return {
        runId,
        sessionId: job.sessionId,
        status: job.status,
        error: job.error,
        cancelRequested: job.cancelRequested,
      };
    },
    cancelRequest: async (runId) => {
      const job = jobs.get(runId);
      if (!job) throw new NotFoundError("reasoning-request-job-not-found");
      if (job.status === "queued") job.status = "cancelled";
      else if (!["completed", "failed", "cancelled"].includes(job.status)) {
        job.cancelRequested = true;
        job.status = "uncertain";
      }
      return {
        runId,
        sessionId: job.sessionId,
        status: job.status,
        cancelRequested: job.cancelRequested,
      };
    },
    reconcileRequest: async (runId) => {
      const job = jobs.get(runId);
      if (!job) throw new NotFoundError("reasoning-request-job-not-found");
      const latest = operations.get(job.sessionId)?.at(-1);
      job.status = latest ? "completed" : "failed";
      if (!latest) job.error = "The interrupted call was acknowledged without a result.";
      const operation: ReasoningOperation = latest ?? {
        operationId: uuid(),
        sessionId: job.sessionId,
        status: "failed",
        candidateRecordId: null,
        evaluationRecordId: null,
        error: job.error,
        requestedModel: job.model,
        requestedEffort: job.effort,
        inputTokens: 0,
        outputTokens: 0,
      };
      return { operation };
    },
    start: async (input) => {
      const sessionId = input.sessionId ?? uuid();
      const commandId = input.commandId ?? uuid();
      const existing = sessions.get(sessionId);
      const session = existing ?? createSession(input.runId, sessionId, commandId, input);
      return {
        sessionId,
        receipt: receipt(
          { sessionId, expectedSessionRevision: session.revision, commandId },
          session,
          session.contextRecordId,
        ),
      };
    },
    get: async (sessionId) => {
      const session = requireSession(sessionId);
      return { session: structuredClone(session), sourceBarriers: barriers.get(sessionId) ?? [] };
    },
    byRun: async (runId) => {
      const sessionId = runs.get(runId);
      const session = sessionId ? sessions.get(sessionId) : null;
      return {
        session: session ? structuredClone(session) : null,
        sourceBarriers: sessionId ? (barriers.get(sessionId) ?? []) : [],
      };
    },
    trace: async (sessionId, afterSequence = 0, limit = 100): Promise<ReasoningTrace> => {
      requireSession(sessionId);
      const all = events.get(sessionId) ?? [];
      const page = all.filter((row) => row.sequence > afterSequence).slice(0, limit);
      const last = page.at(-1)?.sequence ?? afterSequence;
      const ops = operations.get(sessionId) ?? [];
      return {
        trace: {
          events: structuredClone(page),
          nextSequence: all.some((row) => row.sequence > last) ? last : null,
        },
        traceCoverage: "observable-events-operation-journal-provider-receipts-and-actor-steps/1",
        traceManifest: {
          scope: "session-observable-events/1",
          eventSequenceHighWatermark: all.length,
          recordedEventCount: all.length,
          missingRanges: [],
          missingRangeCount: 0,
          providerReceiptGapCount: ops.filter((op) => op.receiptGap).length,
          operationJournalGapCount: ops.filter((op) => op.journalGap).length,
          responseOmits: ["full-provider-prompts", "full-provider-responses"],
        },
        operations: structuredClone(ops),
      };
    },
    graph: async (sessionId): Promise<ReasoningMemoryGraph> => {
      const session = requireSession(sessionId);
      const all = events.get(sessionId) ?? [];
      return {
        status: "current",
        expectedRevision: session.revision,
        projectedRevision: session.revision,
        expectedEventSequence: all.length,
        projectedEventSequence: all.length,
        expectedRecords: session.records.length,
        nodes: [
          {
            id: `session:${session.id}`,
            kind: "session",
            label: "Reasoning session",
            revision: session.revision,
            digest: null,
          },
          ...session.records.map((row) => ({
            id: `record:${row.id}`,
            kind: row.kind,
            label: row.logicalId,
            revision: row.revision,
            digest: row.payloadDigest,
          })),
        ],
        edges: session.records.map((row) => ({
          source: `session:${session.id}`,
          target: `record:${row.id}`,
          kind: "HAS_RECORD",
          revision: null,
          digest: null,
        })),
      };
    },
    execute: async (input) => {
      const session = requireSession(input.sessionId);
      let checkpoint: { id: string; digest: string } | undefined;
      if (input.chainCheckpointId) {
        const view = chainOf(session);
        if (!view.effective.includes(input.chainCheckpointId))
          throw refuse(
            REASONING_CHAIN_ERRORS.checkpointUnavailable,
            "The chosen entry is no longer in the effective chain. Reload the chain and pick again.",
            409,
          );
        checkpoint = { id: input.chainCheckpointId, digest: view.digest };
      }
      return runOperation(session, input, input.commandId ?? uuid(), checkpoint);
    },
    chain: async (sessionId) => chainOf(requireSession(sessionId)),
    reviseChain: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      const invalid = (message: string) =>
        refuse(REASONING_CHAIN_ERRORS.invalidRevision, message, 422);
      if (!REASONING_CHAIN_OPS.includes(input.operation as ReasoningChainOp))
        throw invalid("The chain operation must be replace, insert or strike.");
      const carriesStep = input.operation !== "strike";
      const summary = input.summary?.trim() ?? "";
      if (carriesStep && (summary.length < 1 || summary.length > CHAIN_EDIT_SUMMARY_LIMIT))
        throw invalid(
          `A replacement or inserted step needs 1–${CHAIN_EDIT_SUMMARY_LIMIT} characters.`,
        );
      if (
        input.stepKind !== undefined &&
        !REASONING_STEP_KINDS.includes(input.stepKind as ReasoningStepKind)
      )
        throw invalid("The step kind is not one the engine knows.");
      if (input.operation !== "insert" && !input.targetId)
        throw invalid("Replace and strike name the entry they act on.");
      fence(session, input.expectedSessionRevision);
      const view = chainOf(session);
      const anchorId = input.operation === "insert" ? input.afterId : input.targetId;
      const anchor = anchorId ? view.entries.find((entry) => entry.id === anchorId) : undefined;
      if (anchorId) {
        if (!anchor)
          throw refuse(
            REASONING_CHAIN_ERRORS.entryNotFound,
            "The entry is not in this session's chain.",
            409,
          );
        if (anchor.position < 0 || anchor.struck || anchor.supersededByRecordId)
          throw refuse(
            REASONING_CHAIN_ERRORS.entrySuperseded,
            "The entry was already replaced or struck. Reload the chain.",
            409,
          );
      }
      const target = input.operation === "insert" ? undefined : anchor;
      const row = append(
        session,
        "step",
        `step-${session.records.filter((r) => r.kind === "step").length + 1}`,
        {
          kind: carriesStep ? (input.stepKind ?? "revision") : "revision",
          summary: carriesStep
            ? summary
            : `Struck: ${(target?.summary ?? "").slice(0, 120) || (target?.id ?? "")}`,
          rationaleSummary: input.rationale?.trim() ?? "",
          inputRecordIds: target?.recordId ? [target.recordId] : [],
          causedByEventIds: [],
          evidence: input.evidence ?? [],
          chainOp: input.operation,
          ...(input.operation === "insert"
            ? input.afterId
              ? { afterId: input.afterId }
              : {}
            : { targetId: input.targetId }),
          ...(target?.operationId ? { targetOperationId: target.operationId } : {}),
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    refresh: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const prior = contextOf(session);
      const context = append(
        session,
        "context",
        "context",
        {
          ...(prior?.payload ?? {}),
          manifest: {
            ...((prior?.payload.manifest as Record<string, unknown>) ?? {}),
            query: input.query,
            scopeSlug: input.scopeSlug,
            model: input.model,
            modelContextWindow: input.modelContextWindow,
            outputReserve: input.outputReserve,
          },
        },
        input.commandId ?? uuid(),
        (prior?.revision ?? 0) + 1,
      );
      session.contextRecordId = context.id;
      return receipt(input, session, context.id);
    },
    candidate: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "candidate",
        input.logicalId,
        {
          content: input.content,
          rationale: input.rationale,
          evidence: input.evidence,
          contextRecordId: session.contextRecordId,
          derivedFromRecordIds: input.derivedFromRecordIds ?? [],
          evidenceComplete: input.evidenceComplete ?? true,
        },
        input.commandId ?? uuid(),
        input.revision,
      );
      if (session.currentAcceptanceRecordId) {
        session.currentAcceptanceRecordId = null;
        session.state = "reasoning";
      }
      return receipt(input, session, row.id);
    },
    step: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "step",
        `step-${session.records.filter((r) => r.kind === "step").length + 1}`,
        {
          kind: input.kind,
          summary: input.summary,
          rationaleSummary: input.rationale ?? "",
          inputRecordIds: input.inputRecordIds ?? [],
          causedByEventIds: input.causedByEventIds ?? [],
          evidence: input.evidence ?? [],
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    contribute: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "contribution",
        `contribution-${session.records.filter((r) => r.kind === "contribution").length + 1}`,
        {
          kind: input.kind,
          content: input.content,
          rationale: input.rationale,
          baseCandidateRecordId: input.baseCandidateRecordId ?? null,
          evidence: input.evidence ?? [],
          status: "pending",
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    evaluate: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const hardFail = input.verdicts.some((verdict) => {
        const criterion = session.criteria.find((c) => c.id === verdict.criterionId);
        return verdict.verdict === "fail" && (criterion?.hard ?? true);
      });
      const row = append(
        session,
        "evaluation",
        `evaluation-${session.records.filter((r) => r.kind === "evaluation").length + 1}`,
        {
          candidateRecordId: input.candidateRecordId,
          contextRecordId: input.contextRecordId,
          criteriaDigest: input.criteriaDigest,
          verdicts: input.verdicts,
          rationale: input.rationale,
          qualified: !hardFail && input.verdicts.every((v) => v.verdict !== "unknown"),
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    accept: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "acceptance",
        "acceptance",
        {
          candidateRecordId: input.candidateRecordId,
          evaluationRecordId: input.evaluationRecordId,
        },
        input.commandId ?? uuid(),
        session.records.filter((r) => r.kind === "acceptance").length + 1,
      );
      session.currentAcceptanceRecordId = row.id;
      session.state = "accepted";
      return receipt(input, session, row.id, "accepted");
    },
    assessEvidence: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "evidence-assessment",
        `assessment-${session.records.filter((r) => r.kind === "evidence-assessment").length + 1}`,
        {
          candidateRecordId: input.candidateRecordId,
          evidence: input.evidence,
          relation: input.relation,
          rationale: input.rationale,
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    proposeReplan: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const affected = session.records
        .filter((row) => row.kind === "evaluation" || row.kind === "acceptance")
        .map((row) => row.id);
      const impact = {
        baseSessionRevision: session.revision,
        affectedRecordIds: affected,
        currentAcceptanceRecordId: session.currentAcceptanceRecordId,
      };
      const row = append(
        session,
        "replan-proposal",
        `replan-${session.records.filter((r) => r.kind === "replan-proposal").length + 1}`,
        {
          criteria: input.criteria,
          rationale: input.rationale,
          impact,
          impactDigest: fakeDigest(JSON.stringify(impact)),
        },
        input.commandId ?? uuid(),
      );
      return receipt(input, session, row.id);
    },
    applyReplan: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const proposal = session.records.find((row) => row.id === input.proposalRecordId);
      if (proposal?.kind !== "replan-proposal")
        throw new ApiError("The replan proposal does not exist in this session.", 400);
      if (proposal.payload.impactDigest !== input.impactDigest)
        throw new ApiError("The replan impact changed since it was previewed.", 409);
      const impact = proposal.payload.impact as { affectedRecordIds: string[] };
      invalidate(session, impact.affectedRecordIds, "criteria changed");
      session.criteria = proposal.payload.criteria as ReasoningCriterion[];
      session.criteriaDigest = fakeDigest(JSON.stringify(session.criteria));
      const row = append(
        session,
        "replan",
        "replan",
        { proposalRecordId: proposal.id, criteriaDigest: session.criteriaDigest },
        input.commandId ?? uuid(),
        session.records.filter((r) => r.kind === "replan").length + 1,
      );
      return receipt(input, session, row.id);
    },
    correctSource: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const roots = session.records
        .filter(
          (row) =>
            Array.isArray(row.payload.evidence) &&
            (row.payload.evidence as ReasoningEvidence[]).some(
              (e) => e.sourceKind === "canopy" && e.sourceId === input.sourceNodeId,
            ),
        )
        .map((row) => row.id);
      const affected = [...new Set([...roots, ...dependents(session, roots)])];
      invalidate(session, affected, "source changed");
      session.dependencyEpoch += 1;
      const row = append(
        session,
        "correction",
        `correction-${session.records.filter((r) => r.kind === "correction").length + 1}`,
        {
          sourceKind: "canopy",
          sourceId: input.sourceNodeId,
          sourceEpoch: input.expectedSourceEpoch + 1,
          tombstone: input.tombstone ?? false,
          rationale: input.rationale,
          affectedRecordIds: affected,
          affectedRecordCount: affected.length,
          impactTruncated: false,
        },
        input.commandId ?? uuid(),
      );
      barriers.set(
        session.id,
        (barriers.get(session.id) ?? []).filter((b) => b.sourceId !== input.sourceNodeId),
      );
      return receipt(input, session, row.id);
    },
    activateBranches: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = input.commandId ? branchReceipts.get(input.commandId) : undefined;
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const max = FAKE_REASONING_SUPPORT.limits.maxIndependentBranches ?? 8;
      if (input.members.length < 1 || input.members.length > max)
        throw new ApiError(`A branch group needs 1–${max} members.`, 400);
      const group: ReasoningBranchGroup = {
        id: uuid(),
        sessionId: session.id,
        status: "activated",
        joinMode: input.joinMode ?? "all",
        quorum: input.quorum ?? 0,
        contextRecordId: session.contextRecordId,
        contextDigest: contextOf(session)?.payloadDigest,
        criteriaDigest: session.criteriaDigest,
        dependencyEpoch: session.dependencyEpoch,
        finalReviewReservation: 4096,
        joinedCandidateRecordId: null,
        members: input.members.map((member) => ({
          memberId: member.memberId,
          purpose: member.purpose,
          status: "assigned",
          mandatory: member.mandatory ?? true,
          deadline: member.deadline ?? null,
          attempts: [
            {
              id: uuid(),
              dispatchId: uuid(),
              attemptNumber: 1,
              assignmentEpoch: 0,
              targetSessionKey: member.targetSessionKey,
              workerSessionKey: null,
              status: "assigned",
              reservedTokens: member.tokenReservation,
              chargedTokens: member.tokenReservation,
              candidateRecordId: null,
              createdAt: now(),
            },
          ],
        })),
      };
      groups.set(session.id, [...(groups.get(session.id) ?? []), group]);
      session.revision += 1;
      event(session, "branch-activated", { groupId: group.id, members: group.members.length });
      return branchReceipt(input, session, group.id, "activated");
    },
    acknowledgeBranch: async (input) => {
      const session = requireSession(input.sessionId);
      const group = groupOf(input.sessionId, input.groupId);
      const member = group.members.find((m) => m.attempts.some((a) => a.id === input.attemptId));
      const attempt = member?.attempts.find((a) => a.id === input.attemptId);
      if (!member || !attempt) throw new NotFoundError("The branch attempt does not exist.");
      attempt.assignmentEpoch += 1;
      attempt.workerSessionKey = input.workerSessionKey;
      attempt.status = "acknowledged";
      member.status = "running";
      return branchReceipt(
        {
          sessionId: input.sessionId,
          expectedSessionRevision: session.revision,
          commandId: input.commandId,
        },
        session,
        group.id,
        "acknowledged",
        { attemptId: attempt.id, assignmentEpoch: attempt.assignmentEpoch },
      );
    },
    reportBranch: async (input) => {
      const session = requireSession(input.sessionId);
      const group = groupOf(input.sessionId, input.groupId);
      const member = group.members.find((m) => m.attempts.some((a) => a.id === input.attemptId));
      const attempt = member?.attempts.find((a) => a.id === input.attemptId);
      if (!member || !attempt) throw new NotFoundError("The branch attempt does not exist.");
      if (attempt.assignmentEpoch !== input.assignmentEpoch)
        return branchReceipt(
          {
            sessionId: input.sessionId,
            expectedSessionRevision: session.revision,
            commandId: input.commandId,
          },
          session,
          group.id,
          "late",
          { attemptId: attempt.id, assignmentEpoch: attempt.assignmentEpoch },
        );
      let recordId: string | null = null;
      if (input.status === "completed" && input.content) {
        const row = append(
          session,
          "candidate",
          `branch-${member.memberId}`,
          {
            content: input.content,
            rationale: input.rationale ?? "",
            evidence: pinnedEvidence(session),
            contextRecordId: session.contextRecordId,
            derivedFromRecordIds: [],
            evidenceComplete: true,
            branchGroupId: group.id,
            memberId: member.memberId,
          },
          input.commandId ?? uuid(),
          member.attempts.length,
        );
        recordId = row.id;
        attempt.candidateRecordId = row.id;
        attempt.status = "succeeded";
        member.status = "succeeded";
      } else {
        attempt.status = "failed";
        member.status = "failed";
      }
      return branchReceipt(
        {
          sessionId: input.sessionId,
          expectedSessionRevision: session.revision,
          commandId: input.commandId,
        },
        session,
        group.id,
        attempt.status,
        { attemptId: attempt.id, assignmentEpoch: attempt.assignmentEpoch, recordId },
      );
    },
    retryBranch: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = input.commandId ? branchReceipts.get(input.commandId) : undefined;
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const group = groupOf(input.sessionId, input.groupId);
      const member = group.members.find((m) => m.memberId === input.memberId);
      if (!member) throw new NotFoundError("The branch member does not exist.");
      const current = member.attempts.at(-1);
      if (current?.id !== input.expectedAttemptId)
        throw new ApiError("The member's current attempt changed.", 409);
      const attempt = {
        id: uuid(),
        dispatchId: uuid(),
        attemptNumber: member.attempts.length + 1,
        assignmentEpoch: 0,
        targetSessionKey: input.targetSessionKey,
        workerSessionKey: null,
        status: "assigned",
        reservedTokens: current.reservedTokens ?? 0,
        chargedTokens: current.reservedTokens ?? 0,
        candidateRecordId: null,
        createdAt: now(),
      };
      member.attempts.push(attempt);
      member.status = "assigned";
      session.revision += 1;
      return branchReceipt(input, session, group.id, "retried", {
        attemptId: attempt.id,
        assignmentEpoch: 0,
      });
    },
    joinBranches: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = input.commandId ? branchReceipts.get(input.commandId) : undefined;
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const group = groupOf(input.sessionId, input.groupId);
      const pending = group.members.filter(
        (m) => m.mandatory !== false && m.status !== "succeeded",
      );
      if (group.joinMode === "all" && pending.length)
        throw new ApiError("Every required branch must report before the group joins.", 409);
      const results = group.members
        .filter((m) => m.status === "succeeded")
        .map((m) => ({
          memberId: m.memberId,
          candidateRecordId: m.attempts.at(-1)?.candidateRecordId ?? null,
        }));
      const row = append(
        session,
        "branch-join",
        `join-${group.id.slice(0, 8)}`,
        {
          groupId: group.id,
          results,
          unavailable: pending.map((m) => ({ memberId: m.memberId, status: m.status })),
        },
        input.commandId ?? uuid(),
      );
      group.status = "joined";
      group.joinedCandidateRecordId = results[0]?.candidateRecordId ?? null;
      return branchReceipt(input, session, group.id, "joined", { recordId: row.id });
    },
    cancelBranches: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = input.commandId ? branchReceipts.get(input.commandId) : undefined;
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const group = groupOf(input.sessionId, input.groupId);
      group.status = "cancelled";
      for (const member of group.members) {
        if (member.status !== "succeeded") {
          member.status = "cancelled";
          const attempt = member.attempts.at(-1);
          if (attempt) attempt.status = "cancelled";
        }
      }
      session.revision += 1;
      event(session, "branch-cancelled", { groupId: group.id, rationale: input.rationale });
      return branchReceipt(input, session, group.id, "cancelled");
    },
    branchGroup: async (sessionId, groupId) => {
      requireSession(sessionId);
      const group = (groups.get(sessionId) ?? []).find((row) => row.id === groupId);
      return group ? structuredClone(group) : null;
    },
    branchGroups: async (sessionId) => {
      requireSession(sessionId);
      return structuredClone(groups.get(sessionId) ?? []);
    },
    followupObjective: async (input) => {
      const session = requireSession(input.sessionId);
      const replayed = replay(input);
      if (replayed) return replayed;
      fence(session, input.expectedSessionRevision);
      const row = append(
        session,
        "objective-followup",
        `followup-${input.objectiveId}`,
        {
          objectiveId: input.objectiveId,
          status: input.status,
          observation: input.observation,
          evidence: input.evidence ?? [],
        },
        input.commandId ?? uuid(),
        session.records.filter((r) => r.kind === "objective-followup").length + 1,
      );
      return receipt(input, session, row.id);
    },
    submitPostmortem: async (input) => {
      const existing = postmortems.get(input.runId);
      if (existing) return structuredClone(existing);
      const sessionId = runs.get(input.runId);
      const session = sessionId ? sessions.get(sessionId) : undefined;
      const purpose = contextOf(
        session ?? ({ records: [], contextRecordId: "" } as unknown as ReasoningSession),
      )?.payload.purpose;
      const report: FlowPostmortem = {
        runId: input.runId,
        artifactId: uuid(),
        version: 1,
        status: "queued",
        dreamerAttemptId: null,
        dreamerLeaseExpiresAt: null,
        dreamerError: null,
        reportedAt: now(),
        summary: input.summary,
        outcome: input.outcome,
        lessons: input.lessons,
        purpose: (purpose as FlowPostmortem["purpose"]) ?? null,
        objectiveFollowups: null,
        dreamerReview: null,
        dreamerObjectiveFollowups: null,
      };
      postmortems.set(input.runId, report);
      return structuredClone(report);
    },
    postmortem: async (runId) => {
      const report = postmortems.get(runId);
      return report ? structuredClone(report) : null;
    },
    dreamerInbox: async () =>
      [...postmortems.values()]
        .filter((row) => row.status === "queued")
        .map((row) => structuredClone(row)),
    processPostmortem: async (input) => {
      const report = postmortems.get(input.runId);
      if (!report) throw new NotFoundError("No postmortem was submitted for this run.");
      if (report.status === "uncertain" && !input.acknowledgeUncertain)
        throw new ApiError("Acknowledge the uncertain attempt before retrying the Dreamer.", 409);
      report.dreamerAttemptId = uuid();
      report.status = "processed";
      report.dreamerError = null;
      report.dreamerReview = `Reviewed “${report.summary}”: the outcome “${report.outcome}” follows from the recorded steps. Lessons kept: ${report.lessons.join("; ") || "none recorded"}.`;
      report.dreamerObjectiveFollowups = report.purpose?.objectives.map(
        (objective) =>
          `${objective.outcome}: check the success signal “${objective.successSignal}” after the next run.`,
      ) ?? ["Record the observed outcome after the next run."];
      return structuredClone(report);
    },
    dreamerExperiments: async (runId) => structuredClone(experiments.get(runId) ?? []),
    planDreamerExperiment: async (input) =>
      experimentArtifact(input.runId, input.experimentId, "plan", {
        hypothesis: input.hypothesis,
        metric: input.metric,
        candidateSlug: input.candidateFlowSlug,
        candidateVersion: input.candidateVersion,
      }),
    measureDreamerExperiment: async (input) =>
      experimentArtifact(input.runId, input.experimentId, "measure", {
        candidateRunId: input.candidateRunId,
        baselineValue: 0.6,
        candidateValue: 0.9,
        improved: true,
      }),
    publishDreamerExperiment: async (input) =>
      experimentArtifact(input.runId, input.experimentId, "publish", { version: 2 }),
    rollbackDreamerExperiment: async (input) =>
      experimentArtifact(input.runId, input.experimentId, "rollback", { version: 3 }),
  };
  return api;
}
